import { useState } from 'react';
import { useInvites, useCreateInvite, useRevokeInvite } from '../../hooks/useOnboarding.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import { centsToUsd } from '../../lib/format.js';

// Lists pending invites + a "+ New invite" button that opens the wizard.
// Once an invite is created, its URL is shown for copying.
export default function OnboardingPage() {
  const { data: invites = [], isLoading } = useInvites();
  const [showWizard, setShowWizard] = useState(false);
  const [latestInvite, setLatestInvite] = useState(null);

  return (
    <div>
      <div className="range-bar">
        <button className="btn-sm primary" onClick={() => setShowWizard(true)}>
          + New invite
        </button>
        <span style={{ color: 'var(--warm-gray)', fontSize: 11 }}>
          {invites.length} pending
        </span>
      </div>

      {latestInvite && (
        <div className="panel" style={{ background: 'var(--surface-container)', borderLeft: '3px solid var(--orange)', marginBottom: 12 }}>
          <h3 className="panel-title" style={{ marginBottom: 8 }}>
            <span><i className="ph ph-link-simple" /> Invite link for {latestInvite.display_name}</span>
          </h3>
          <input className="field-input" readOnly value={latestInvite.invite_url}
            onClick={ev => ev.target.select()} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }} />
          <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginTop: 6 }}>
            Send this to the new hire via Discord DM. Link expires in 30 days.
          </div>
        </div>
      )}

      {showWizard && <Wizard onClose={() => setShowWizard(false)} onCreated={setLatestInvite} />}

      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : invites.length === 0 ? (
        <div className="loading">No pending invites.</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Role</th>
              <th className="num col-meta">Rate</th>
              <th>Created</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invites.map(i => <InviteRow key={i.token} i={i} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function InviteRow({ i }) {
  const revoke = useRevokeInvite();
  const { show } = useError();
  const onRevoke = async () => {
    if (!confirm(`Revoke invite for ${i.display_name}?`)) return;
    try { await revoke.mutateAsync(i.token); } catch (err) { show(err.message); }
  };
  return (
    <tr>
      <td>{i.display_name}</td>
      <td><span className="badge">{i.employment_type}</span></td>
      <td><span className={'badge' + (i.role === 'admin' ? ' orange' : '')}>{i.role}</span></td>
      <td className="num col-meta">{centsToUsd(i.hourly_rate_cents)}/h</td>
      <td>{new Date(i.created_at).toLocaleDateString()}</td>
      <td>{new Date(i.expires_at).toLocaleDateString()}</td>
      <td style={{ textAlign: 'right' }}>
        <button className="btn-sm" onClick={onRevoke} disabled={revoke.isPending}>Revoke</button>
      </td>
    </tr>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────
function Wizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    display_name: '',
    legal_name: '',
    preferred_name: '',
    email: '',
    phone: '',
    dob: '',
    address: { street: '', city: '', state: '', zip: '' },
    start_date: '',
    employment_type: 'w2',
    role: 'crew',
    hourly_rate_cents: 0,
    ot_threshold_hrs: 40,
    ot_multiplier: 1.5,
    tax_id: '',
    bank_routing: '',
    bank_account: '',
    bank_account_type: 'checking',
  });
  const create = useCreateInvite();
  const { show } = useError();

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const updateAddress = (patch) => setForm(prev => ({ ...prev, address: { ...prev.address, ...patch } }));

  const submit = async () => {
    try {
      const payload = {
        ...form,
        dob: form.dob ? Date.parse(form.dob + 'T00:00:00Z') : null,
        start_date: form.start_date ? Date.parse(form.start_date + 'T00:00:00Z') : null,
        hourly_rate_cents: Math.round(Number(form.hourly_rate_cents) * 100) || 0,
        // Don't send empty strings for optional values.
        tax_id: form.tax_id || undefined,
        bank_routing: form.bank_routing || undefined,
        bank_account: form.bank_account || undefined,
      };
      const result = await create.mutateAsync(payload);
      onCreated({ ...result, display_name: form.display_name });
      onClose();
    } catch (err) { show(err.message); }
  };

  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <h3 className="panel-title">
        <span><i className="ph ph-user-plus" /> New hire — Step {step} of 5</span>
        <button className="btn-sm" type="button" onClick={onClose}>Cancel</button>
      </h3>

      {step === 1 && <Step1 form={form} update={update} updateAddress={updateAddress} />}
      {step === 2 && <Step2 form={form} update={update} />}
      {step === 3 && (form.employment_type === 'w2'
        ? <Step3W2 form={form} update={update} />
        : <Step3Contractor form={form} update={update} />)}
      {step === 4 && <Step4 form={form} update={update} />}
      {step === 5 && <Step5 form={form} update={update} />}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
        <button className="btn-sm" type="button" disabled={step === 1} onClick={() => setStep(step - 1)}>
          ← Back
        </button>
        {step < 5 ? (
          <button className="btn-sm primary" type="button" onClick={() => setStep(step + 1)}>
            Next →
          </button>
        ) : (
          <button className="btn-sm primary" type="button" onClick={submit} disabled={create.isPending || !form.display_name}>
            {create.isPending ? 'Creating…' : 'Create invite'}
          </button>
        )}
      </div>
    </div>
  );
}

function Step1({ form, update, updateAddress }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        Identity
      </h4>
      <Grid2>
        <Field label="Display name (required)"><input className="field-input" value={form.display_name} onChange={e => update({ display_name: e.target.value })} required /></Field>
        <Field label="Legal name"><input className="field-input" value={form.legal_name} onChange={e => update({ legal_name: e.target.value })} /></Field>
        <Field label="Preferred name"><input className="field-input" value={form.preferred_name} onChange={e => update({ preferred_name: e.target.value })} /></Field>
        <Field label="Email"><input className="field-input" type="email" value={form.email} onChange={e => update({ email: e.target.value })} /></Field>
        <Field label="Phone"><input className="field-input" type="tel" value={form.phone} onChange={e => update({ phone: e.target.value })} /></Field>
        <Field label="Date of birth"><input className="field-input" type="date" value={form.dob} onChange={e => update({ dob: e.target.value })} /></Field>
      </Grid2>
      <Field label="Street"><input className="field-input" value={form.address.street} onChange={e => updateAddress({ street: e.target.value })} /></Field>
      <Grid3>
        <Field label="City"><input className="field-input" value={form.address.city} onChange={e => updateAddress({ city: e.target.value })} /></Field>
        <Field label="State"><input className="field-input" maxLength={2} value={form.address.state} onChange={e => updateAddress({ state: e.target.value })} /></Field>
        <Field label="ZIP"><input className="field-input" value={form.address.zip} onChange={e => updateAddress({ zip: e.target.value })} /></Field>
      </Grid3>
    </>
  );
}

function Step2({ form, update }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        Classification
      </h4>
      <Grid2>
        <Field label="Employment type">
          <select className="field-select" value={form.employment_type} onChange={e => update({ employment_type: e.target.value })}>
            <option value="w2">W-2 employee</option>
            <option value="1099">1099 contractor</option>
          </select>
        </Field>
        <Field label="Start date"><input className="field-input" type="date" value={form.start_date} onChange={e => update({ start_date: e.target.value })} /></Field>
        <Field label="Role">
          <select className="field-select" value={form.role} onChange={e => update({ role: e.target.value })}>
            <option value="crew">crew</option>
            <option value="foreman">foreman</option>
            <option value="bookkeeper">bookkeeper</option>
            <option value="admin">admin</option>
          </select>
        </Field>
      </Grid2>
    </>
  );
}

function Step3W2({ form, update }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        W-4 (federal withholding)
      </h4>
      <Field label="SSN (encrypted at rest — only last 4 are visible)">
        <input className="field-input" value={form.tax_id} onChange={e => update({ tax_id: e.target.value })} placeholder="XXX-XX-XXXX" />
      </Field>
      <Hint>I-9 verification happens out of band — collect ID documents in person, then upload via the Documents page.</Hint>
    </>
  );
}

function Step3Contractor({ form, update }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        W-9 (contractor)
      </h4>
      <Field label="SSN or EIN (encrypted at rest — only last 4 are visible)">
        <input className="field-input" value={form.tax_id} onChange={e => update({ tax_id: e.target.value })} placeholder="XXX-XX-XXXX or XX-XXXXXXX" />
      </Field>
      <Hint>1099-NEC issuance at year-end uses this tax ID. Stripe Connect would replace this flow when integrated.</Hint>
    </>
  );
}

function Step4({ form, update }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        Direct deposit
      </h4>
      <Grid2>
        <Field label="Routing number"><input className="field-input" value={form.bank_routing} onChange={e => update({ bank_routing: e.target.value })} /></Field>
        <Field label="Account type">
          <select className="field-select" value={form.bank_account_type} onChange={e => update({ bank_account_type: e.target.value })}>
            <option value="checking">checking</option>
            <option value="savings">savings</option>
          </select>
        </Field>
      </Grid2>
      <Field label="Account number"><input className="field-input" value={form.bank_account} onChange={e => update({ bank_account: e.target.value })} /></Field>
      <Hint>Only the last 4 digits will be visible after save. The hire can update banking themselves from the Pay page.</Hint>
    </>
  );
}

function Step5({ form, update }) {
  return (
    <>
      <h4 style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.1rem', textTransform: 'uppercase', color: 'var(--orange)' }}>
        Rate
      </h4>
      <Grid3>
        <Field label="Hourly rate ($)"><input className="field-input" type="number" step="0.01" min="0" value={form.hourly_rate_cents} onChange={e => update({ hourly_rate_cents: e.target.value })} /></Field>
        <Field label="OT threshold (hrs/wk)"><input className="field-input" type="number" step="1" min="0" max="168" value={form.ot_threshold_hrs} onChange={e => update({ ot_threshold_hrs: Number(e.target.value) })} /></Field>
        <Field label="OT multiplier"><input className="field-input" type="number" step="0.1" min="1" max="3" value={form.ot_multiplier} onChange={e => update({ ot_multiplier: Number(e.target.value) })} /></Field>
      </Grid3>
      <Hint>The rate becomes effective when the hire signs in via the invite link. Adjust later from the Crew page.</Hint>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{children}</div>;
}
function Grid3({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>{children}</div>;
}
function Hint({ children }) {
  return <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginTop: 8 }}>{children}</div>;
}
